const TEMP_TOKEN_PARAM = "temp_token";

const stripTrailingSlash = (value: string): string =>
    value.endsWith("/") ? value.slice(0, -1) : value;

// The new app's "home" lives at "/", whereas the old app's authenticated entry
// point is "/dashboard". Map old-app paths to their new-app equivalent before
// handing off so deep-linked redirects don't land on a stale URL.
const mapOldPathToNewPath = (pathname: string): string => {
    if (pathname === "/dashboard" || pathname === "/dashboard/") return "/";
    return pathname;
};

const stripTempTokenFromQuery = (search: string): string => {
    const params = new URLSearchParams(search);
    if (!params.has(TEMP_TOKEN_PARAM)) return search;
    params.delete(TEMP_TOKEN_PARAM);
    const serialized = params.toString();
    return serialized ? `?${serialized}` : "";
};

const stripTempTokenFromHash = (hash: string): string => {
    if (!hash.includes(`${TEMP_TOKEN_PARAM}=`)) return hash;
    const inner = hash.startsWith("#") ? hash.slice(1) : hash;
    const remaining = inner
        .split("&")
        .filter((part) => !part.startsWith(`${TEMP_TOKEN_PARAM}=`))
        .join("&");
    return remaining ? `#${remaining}` : "";
};

/**
 * Resolve the new buyer-app base URL. Returns null when
 * `VITE_NEW_BUYER_APP_URL` is missing — there is no localhost fallback,
 * because falling back in production would hard-redirect users (and leak a
 * freshly minted `temp_token`) to a loopback origin. Callers must treat null
 * as "redirect unavailable". Local dev gets the URL from `.env.local`.
 */
export const getNewAppUrl = (): string | null => {
    const fromEnv = import.meta.env.VITE_NEW_BUYER_APP_URL as
        | string
        | undefined;
    if (!fromEnv) return null;
    return stripTrailingSlash(fromEnv);
};

/**
 * Resolve the origin-isolated masquerade base URL by prefixing the buyer-app
 * hostname with `masquerade.` (scheme + port preserved):
 *
 *   http://localhost:3000         → http://masquerade.localhost:3000
 *   https://new-staging.opply.com → https://masquerade.new-staging.opply.com
 *   https://app.opply.com         → https://masquerade.app.opply.com
 *
 * Serving masquerade on its own origin gives the exchanged buyer token its own
 * localStorage bucket, so it can never clobber the MoR session that lives on
 * the buyer-app origin (SC-3953). This convention MUST stay in sync with the
 * buyer app's `NEXT_PUBLIC_MASQUERADE_HOST` (same `masquerade.<host>` shape).
 *
 * Returns null when `VITE_NEW_BUYER_APP_URL` is missing or unparseable —
 * callers must treat null as "masquerade hand-off unavailable".
 */
export const getMasqueradeAppUrl = (): string | null => {
    const base = getNewAppUrl();
    if (!base) return null;
    try {
        const url = new URL(base);
        url.hostname = `masquerade.${url.hostname}`;
        return stripTrailingSlash(url.origin);
    } catch {
        return null;
    }
};

/**
 * Build the cross-app destination URL. The `temp_token` is appended to the
 * URL fragment (not the query string) so it never reaches a server log,
 * reverse proxy, WAF, or Referer header during the cross-app hop. Existing
 * fragment content is preserved by appending `&temp_token=...` after it; the
 * destination parses the fragment with `URLSearchParams`.
 */
export const getNewAppUrlForCurrentPath = (
    tempToken?: string
): string | null => {
    const base = getNewAppUrl();
    if (!base) return null;

    const { pathname, search, hash } = window.location;
    const targetPath = mapOldPathToNewPath(pathname);

    // Always strip any incoming `temp_token` first so a fallback redirect
    // (e.g. after a mint failure) never silently forwards a token we didn't
    // mint here, and so we never produce a URL with the token in two places.
    const cleanedSearch = stripTempTokenFromQuery(search);
    const cleanedHash = stripTempTokenFromHash(hash);

    if (!tempToken) {
        return `${base}${targetPath}${cleanedSearch}${cleanedHash}`;
    }

    const tokenPair = `${TEMP_TOKEN_PARAM}=${encodeURIComponent(tempToken)}`;
    const finalHash = cleanedHash
        ? `${cleanedHash}&${tokenPair}`
        : `#${tokenPair}`;
    return `${base}${targetPath}${cleanedSearch}${finalHash}`;
};
